import React from "react";
import { Card, CardContent } from "../ui/card";
import { Users, Target, Calendar, TrendingUp } from "lucide-react";
import { PpcFarmStatistics } from "../../types/ppcFarmTypes";
import { Skeleton } from "../ui/skeleton";

interface PpcFarmMetricsBarProps {
  statistics: PpcFarmStatistics | null;
  loading: boolean;
  error?: string | null;
}

const PpcFarmMetricsBar: React.FC<PpcFarmMetricsBarProps> = ({
  statistics,
  loading,
  error,
}) => {
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "Never";

    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-16" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <TrendingUp className="h-4 w-4" />
            <span className="text-sm font-medium">
              Error loading metrics: {error}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!statistics) {
    return null;
  }

  const metrics = [
    {
      label: "Total Sellers",
      value: statistics.totalRecords.toLocaleString(),
      icon: Users,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: "Unique Cohorts",
      value: statistics.uniqueCohorts.toLocaleString(),
      icon: Target,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      label: "Unique Sellers",
      value: statistics.uniqueSellers.toLocaleString(),
      icon: TrendingUp,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
    {
      label: "Last Updated",
      value: formatDate(statistics.lastUpdated),
      icon: Calendar,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {metrics.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <IconComponent className={`h-4 w-4 ${metric.color} mr-2`} />
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {metric.label}
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PpcFarmMetricsBar;
